import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { parsePartsCsv, partsCsvTemplate, type CsvParseResult } from '@/lib/csv';
import { batchCreateParts } from '@/lib/parts';
import AdminLayout from '@/components/admin/AdminLayout';

export default function BulkImport() {
  const navigate = useNavigate();
  const [parseResult, setParseResult] = useState<CsvParseResult | null>(null);
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState<string | null>(null);
  const [importErr, setImportErr] = useState<string | null>(null);

  async function handleFile(file: File) {
    setImportMsg(null);
    setImportErr(null);
    setParseResult(null);
    setParsing(true);
    try {
      const result = await parsePartsCsv(file);
      setParseResult(result);
    } catch (err) {
      setImportErr(err instanceof Error ? err.message : 'Parse failed');
    } finally {
      setParsing(false);
    }
  }

  async function handleImport() {
    if (!parseResult) return;
    const drafts = parseResult.rows.map((r) => r.draft).filter((d): d is NonNullable<typeof d> => !!d);
    if (drafts.length === 0) {
      setImportErr('No valid rows to import.');
      return;
    }
    setImporting(true);
    setImportErr(null);
    try {
      const count = await batchCreateParts(drafts);
      setImportMsg(`Imported ${count} parts.`);
      setParseResult(null);
    } catch (err) {
      setImportErr(err instanceof Error ? err.message : 'Import failed');
    } finally {
      setImporting(false);
    }
  }

  function downloadTemplate() {
    const csv = partsCsvTemplate();
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'swc-parts-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <AdminLayout>
      <div className="row between" style={{ marginBottom: 16 }}>
        <div>
          <div className="eyebrow">Bulk CSV Import</div>
          <h1 className="h-title" style={{ margin: 0 }}>Import Parts from CSV</h1>
        </div>
        <button type="button" className="btn dark sm" onClick={() => navigate('/admin/inventory')}>← Back</button>
      </div>

      <p className="lead" style={{ marginBottom: 24 }}>
        Upload a CSV of parts. Rows are validated client-side before any Firestore write — invalid
        rows are flagged and the import only commits the valid ones.
      </p>

      <div className="card-elev" style={{ marginBottom: 24 }}>
        <div className="row between" style={{ marginBottom: 14 }}>
          <h2 className="h-section" style={{ margin: 0 }}>1. CSV Template</h2>
          <button type="button" className="btn ghost sm" onClick={downloadTemplate}>↓ Download Template</button>
        </div>
        <p style={{ fontSize: 13, color: 'var(--text-dim)' }}>
          Required columns: <code style={{ fontFamily: 'var(--mono)', color: 'var(--gold)' }}>id, sku, title, year, category, iconId, type, condition, price, weight</code>.
          Optional: <code style={{ fontFamily: 'var(--mono)', color: 'var(--gold)' }}>casting, dateCode, fitment, description, verifiedBy, verifiedDate, authNotes, stock, status</code>.
          Fitment is pipe-separated.
        </p>
      </div>

      <div className="card-elev" style={{ marginBottom: 24 }}>
        <h2 className="h-section">2. Upload</h2>
        <label className="btn block" style={{ cursor: 'pointer', maxWidth: 320 }}>
          {parsing ? 'Parsing…' : 'Choose CSV file'}
          <input
            type="file"
            accept=".csv,text/csv"
            style={{ display: 'none' }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleFile(f);
              e.target.value = '';
            }}
            disabled={parsing}
          />
        </label>
        {importMsg && <div className="auth-success" style={{ marginTop: 14 }}><strong>Done.</strong><p>{importMsg}</p></div>}
        {importErr && <div className="auth-error" style={{ marginTop: 14 }}>{importErr}</div>}
      </div>

      {parseResult && (
        <div className="card-elev">
          <div className="row between" style={{ marginBottom: 14 }}>
            <h2 className="h-section" style={{ margin: 0 }}>3. Review</h2>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.15em' }}>
              <span style={{ color: 'var(--good)' }}>{parseResult.validCount} VALID</span>
              {parseResult.errorCount > 0 && (
                <span style={{ color: 'var(--danger)', marginLeft: 14 }}>{parseResult.errorCount} ERRORS</span>
              )}
            </div>
          </div>

          {parseResult.errorCount > 0 && (
            <div style={{ background: 'var(--bg)', border: '1px solid var(--line-2)', padding: 14, marginBottom: 16, maxHeight: 200, overflowY: 'auto' }}>
              {parseResult.rows
                .filter((r) => r.errors.length > 0)
                .map((r) => (
                  <div key={r.row} style={{ fontSize: 12, color: 'var(--danger)', fontFamily: 'var(--mono)', marginBottom: 6 }}>
                    Row {r.row}: {r.errors.join('; ')}
                  </div>
                ))}
            </div>
          )}

          {parseResult.validCount > 0 && (
            <button type="button" className="btn" onClick={handleImport} disabled={importing}>
              {importing ? 'Importing…' : `Import ${parseResult.validCount} Valid Parts →`}
            </button>
          )}
        </div>
      )}
    </AdminLayout>
  );
}
